import React, { useState, useRef, useEffect } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { ChevronDown, Check, Search, X } from 'lucide-react';
import { SearchableDropdownProps, SearchableDropdownOption } from '../../types';

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  searchPlaceholder = 'Search...',
  disabled = false,
  error,
  autoComplete,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Ensure value is always a string to prevent controlled/uncontrolled warning
  const controlledValue = value ?? '';
  const selectedOption = options.find(option => option.value === controlledValue);

  useEffect(() => {
    const filtered = options.filter(option =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase()) &&
      !option.disabled
    );
    setFilteredOptions(filtered);
  }, [options, searchTerm]);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (option: SearchableDropdownOption) => {
    if (!option.disabled) {
      onChange(option.value);
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setSearchTerm('');
  };

  return (
    <div className="relative">
      {/* Hidden native select for browser autocomplete */}
      {autoComplete && (
        <select
          value={controlledValue}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          className="absolute opacity-0 pointer-events-none"
          tabIndex={-1}
          aria-hidden="true"
        >
          <option value="">Select...</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      )}

      <Listbox value={selectedOption} onChange={handleSelect} disabled={disabled}>
        <div className="relative">
          <Listbox.Button
            className={`relative w-full cursor-default rounded-md border bg-white dark:bg-gray-700 py-2 pl-3 pr-10 text-left shadow-sm focus:border-accent-color focus:outline-none focus:ring-1 focus:ring-accent-color sm:text-sm ${
              error ? 'border-red-300' : 'border-gray-300 dark:border-gray-600'
            } ${disabled ? 'bg-gray-50 dark:bg-gray-600 cursor-not-allowed' : 'hover:bg-gray-50 dark:hover:bg-gray-600'}`}
            onClick={() => setIsOpen(!isOpen)}
          >
            <span className={`block truncate ${selectedOption ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'}`}>
              {selectedOption ? selectedOption.label : placeholder}
            </span>
            <span className="absolute inset-y-0 right-0 flex items-center pr-2">
              {controlledValue && (
                <span
                  onClick={handleClear}
                  className="mr-1 p-1 hover:bg-gray-100 dark:hover:bg-gray-500 rounded cursor-pointer pointer-events-auto"
                >
                  <X className="h-4 w-4 text-gray-400 dark:text-gray-300" />
                </span>
              )}
              <ChevronDown className="h-4 w-4 text-gray-400 dark:text-gray-300 pointer-events-none" />
            </span>
          </Listbox.Button>

          <Transition
            show={isOpen}
            as={React.Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-gray-700 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
              {/* Search input */}
              <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-600">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-300" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder={searchPlaceholder}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-8 pr-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-accent-color focus:border-accent-color bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              </div>

              {/* Options */}
              {filteredOptions.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">No options found</div>
              ) : (
                filteredOptions.map((option) => (
                  <Listbox.Option
                    key={option.value}
                    value={option}
                    className={({ active }) =>
                      `relative cursor-default select-none py-2 pl-3 pr-9 ${
                        active ? 'bg-accent-color text-white' : 'text-gray-900 dark:text-gray-100'
                      } ${option.disabled ? 'opacity-50 cursor-not-allowed' : ''}`
                    }
                  >
                    {({ selected, active }) => (
                      <>
                        <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                          {option.label}
                        </span>
                        {selected ? (
                          <span
                            className={`absolute inset-y-0 right-0 flex items-center pr-4 ${
                              active ? 'text-white' : 'text-accent-color'
                            }`}
                          >
                            <Check className="h-4 w-4" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </Listbox.Option>
                ))
              )}
            </Listbox.Options>
          </Transition>
        </div>
      </Listbox>

      {error && (
        <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>
      )}
    </div>
  );
};

export default SearchableDropdown;
